import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import Collapse from "@mui/material/Collapse";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, role, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const [currentRole, setCurrentRole] = useState(role);
  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const [open, setOpen] = useState({});

  // useEffect(() => {
  //   const path = location.pathname;
  //   const activeRoutes = {};

  //   routes.forEach((route) => {
  //     if (route.collapse) {
  //       route.collapse.forEach((subRoute) => {
  //         if (subRoute.route === path) {
  //           activeRoutes[route.key] = true;
  //           activeRoutes[subRoute.key] = true;
  //         }
  //       });
  //     } else if (route.route === path) {
  //       activeRoutes[route.key] = true;
  //     }
  //   });

  //   setOpen(activeRoutes);
  // }, [location]);

  const handleToggle = (key) => {
    setOpen((prevOpen) => ({ ...prevOpen, [key]: !prevOpen[key] }));
  };

  const renderMenu = (menuItems, open, handleToggle) => {
    return menuItems.map(({ type, name, icon, title, noCollapse, key, href, route, collapse }) => {
      let menuElement;
      if (type === "collapse") {
        const isListOpen = open[key] || false; // Check if the current item is expanded
        menuElement = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              icon={icon}
              name={name}
              key={key}
              onClick={() => handleToggle(key)}
              active={key === collapseName}
              noCollapse={noCollapse}
              isOpen={isListOpen}
              hasChildren={!!collapse}
            />

            {collapse && (
              <Collapse in={isListOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {renderMenu(collapse, open, handleToggle)}
                </List>
              </Collapse>
            )}
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <ListItem component="div" button onClick={() => handleToggle(key)}>
              <SidenavCollapse
                key={key}
                icon={icon}
                name={name}
                component={Link}
                to={route}
                active={key === collapseName}
                noCollapse={noCollapse}
                isOpen={isListOpen}
                hasChildren={!!collapse}
              />
            </ListItem>
            {collapse && (
              <Collapse in={isListOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {renderMenu(collapse, open, handleToggle)}
                </List>
              </Collapse>
            )}
          </NavLink>
        );
      } else if (type === "title") {
        menuElement = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        menuElement = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }
      return menuElement;
    });
  };

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to={currentRole === "Police" ? "/police-dashboard" : "/"}
          display="flex"
          alignItems="center"
        >
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderMenu(routes, open, handleToggle)}</List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  role: PropTypes.string.isRequired,
};

export default Sidenav;
